import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AutoComplete, Button, DatePicker, Form, Input, InputNumber, Modal, Popover, Radio, Space, Tag, Typography, message } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import AircraftSelector from "../../../../../../components/common/AircraftSelector";
import BatchActionsBar from "../../../../../../components/common/BatchActionsBar";
import { useAircrafts } from "../../../../../../hooks/useReferenceData";
import { formatScheduleDraftError } from "../../../lib/errorUtils";
import { BATCH_ADD_FLIGHTS, BATCH_DELETE_FLIGHTS, BATCH_UPDATE_FLIGHT_FIELDS } from "../../../lib/editCommand";
import { normalizeAirportCode } from "../../../lib/routeUtils";
import { isRecommendedStcType, normalizeStcTypeEmptyValue, normalizeStcTypeValue, RECOMMENDED_STC_TYPE_OPTIONS, STC_CUSTOM_VALUE_WARNING } from "../../../constants/scheduleDraftOptions";
import { BATCH_EDITABLE_COLUMNS, getSelectionColumnLabel, parseCellKey } from "./timetableSelection";

const { Text } = Typography;

dayjs.extend(utc);

const AIRCRAFT_BATCH_COLUMN = "aircraft_reg";
const TEXT_BATCH_COLUMNS = new Set(["flight_number", "remarks"]);
const AIRPORT_BATCH_COLUMNS = new Set(["departure_airport", "arrival_airport"]);
const STC_BATCH_COLUMN = "stc_type";
const AIRPORT_CODE_PATTERN = /^[A-Z]{3}$/;
const ROUTE_STRATEGY_OPTIONS = [
    { label: "默认航路", value: "default" },
];

function getRowFlightId(row) {
    if (String(row?.rawFlight?.change_status || row?.changeStatus || "").toUpperCase() === "DELETED") {
        return null;
    }
    return row?._rowType === "flight" && row?.rawFlight?.id != null ? String(row.rawFlight.id) : null;
}

function computeSelectionSummary(selectedCells, rows) {
    const visibleFlightIds = rows.map(getRowFlightId).filter(Boolean);
    const visibleFlightIdSet = new Set(visibleFlightIds);
    const flightIdSet = new Set();
    const columnSet = new Set();
    let selectedCellCount = 0;

    Array.from(selectedCells || []).forEach((cellKey) => {
        const parsed = parseCellKey(cellKey);
        if (!parsed || !visibleFlightIdSet.has(parsed.flightId)) {
            return;
        }

        selectedCellCount += 1;
        flightIdSet.add(parsed.flightId);
        columnSet.add(parsed.columnKey);
    });

    return {
        selectedCellCount,
        affectedFlightIds: visibleFlightIds.filter((flightId) => flightIdSet.has(flightId)),
        affectedColumns: Array.from(columnSet),
    };
}

function getSelectedFlights(rows, affectedFlightIds) {
    const affectedFlightIdSet = new Set(affectedFlightIds.map(String));
    return rows
        .filter((row) => row?._rowType === "flight" && row?.rawFlight?.id != null)
        .filter((row) => affectedFlightIdSet.has(String(row.rawFlight.id)))
        .map((row) => row.rawFlight);
}

function getAirportPairKey(departureAirport, arrivalAirport) {
    return `${departureAirport}:${arrivalAirport}`;
}

function formatAirportPair(departureAirport, arrivalAirport) {
    return `${departureAirport || "--"}→${arrivalAirport || "--"}`;
}

function buildSelectedAirportPairGroups(selectedFlights) {
    const groupsByKey = new Map();

    selectedFlights.forEach((flight) => {
        const departureAirport = normalizeAirportCode(flight?.departure_airport);
        const arrivalAirport = normalizeAirportCode(flight?.arrival_airport);
        const key = getAirportPairKey(departureAirport, arrivalAirport);

        if (!groupsByKey.has(key)) {
            groupsByKey.set(key, {
                key,
                departureAirport,
                arrivalAirport,
                flightIds: [],
            });
        }

        groupsByKey.get(key).flightIds.push(String(flight.id));
    });

    return Array.from(groupsByKey.values()).map((group) => ({
        ...group,
        count: group.flightIds.length,
    }));
}

function buildLineSwitchTargets(groups, currentTargets = {}) {
    return Object.fromEntries(groups.map((group) => [
        group.key,
        currentTargets[group.key] || { targetDepartureAirport: "", targetArrivalAirport: "" },
    ]));
}

function getLineSwitchGroupState(group, target = {}) {
    const targetDepartureAirport = normalizeAirportCode(target.targetDepartureAirport);
    const targetArrivalAirport = normalizeAirportCode(target.targetArrivalAirport);
    const hasTargetDepartureAirport = targetDepartureAirport !== "";
    const hasTargetArrivalAirport = targetArrivalAirport !== "";
    const effectiveDepartureAirport = hasTargetDepartureAirport ? targetDepartureAirport : group.departureAirport;
    const effectiveArrivalAirport = hasTargetArrivalAirport ? targetArrivalAirport : group.arrivalAirport;
    const hasInvalidInput = (hasTargetDepartureAirport && !AIRPORT_CODE_PATTERN.test(targetDepartureAirport))
        || (hasTargetArrivalAirport && !AIRPORT_CODE_PATTERN.test(targetArrivalAirport))
        || ((hasTargetDepartureAirport || hasTargetArrivalAirport) && (!AIRPORT_CODE_PATTERN.test(effectiveDepartureAirport) || !AIRPORT_CODE_PATTERN.test(effectiveArrivalAirport)));

    return {
        hasInvalidInput,
        hasEffectiveChange: !hasInvalidInput && (
            (hasTargetDepartureAirport && targetDepartureAirport !== group.departureAirport)
            || (hasTargetArrivalAirport && targetArrivalAirport !== group.arrivalAirport)
        ),
    };
}

function getLineSwitchReadiness(groups, targets = {}) {
    return groups.reduce((readiness, group) => {
        const groupState = getLineSwitchGroupState(group, targets[group.key]);
        return {
            hasEffectiveChange: readiness.hasEffectiveChange || groupState.hasEffectiveChange,
            hasInvalidInput: readiness.hasInvalidInput || groupState.hasInvalidInput,
        };
    }, { hasEffectiveChange: false, hasInvalidInput: false });
}

function buildDateTimeOnDate(targetDate, sourceTime) {
    return targetDate
        .hour(sourceTime.hour())
        .minute(sourceTime.minute())
        .second(sourceTime.second())
        .millisecond(sourceTime.millisecond())
        .toISOString();
}

function createClientFlightId() {
    if (typeof window !== "undefined" && window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }

    return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (char) => (
        Number(char) ^ (Math.floor(Math.random() * 16) & (15 >> (Number(char) / 4)))
    ).toString(16));
}

function buildCopiedFlightValues(flight, startDate, copyIndex, targetTrackId) {
    const sourceStd = dayjs.utc(flight?.std);
    const sourceSta = dayjs.utc(flight?.sta);

    if (!sourceStd.isValid() || !sourceSta.isValid()) {
        throw new Error(`航班 ${flight?.flight_number || flight?.id || ""} 缺少有效 STD/STA，无法复制`.trim());
    }

    const baseDate = dayjs.utc(startDate.format("YYYY-MM-DD")).add(copyIndex * 7, "day");
    const rawStaDayOffset = sourceSta.startOf("day").diff(sourceStd.startOf("day"), "day");
    const staDayOffset = rawStaDayOffset === 0 && sourceSta.isBefore(sourceStd) ? 1 : rawStaDayOffset;

    return {
        track_id: targetTrackId,
        aircraft_reg: flight.aircraft_reg,
        flight_number: flight.flight_number,
        departure_airport: flight.departure_airport,
        arrival_airport: flight.arrival_airport,
        flight_route_id: flight.flight_route_id ?? null,
        std: buildDateTimeOnDate(baseDate, sourceStd),
        sta: buildDateTimeOnDate(baseDate.add(staDayOffset, "day"), sourceSta),
        stc_type: flight.stc_type,
        block_time_minutes: flight.block_time_minutes,
        is_fixed: Boolean(flight.is_fixed),
        remarks: flight.remarks,
    };
}

const TimetableBatchActionBar = ({
    draftId,
    rows = [],
    selectedCells,
    currentEditingTrackId,
    defaultCopyStartDate,
    canUpdateFlight,
    canDeleteFlight,
    executeCommand,
    onClearSelection,
    onBatchShiftTime,
    onBatchChangeAirport,
    onBatchSwitchLine,
    batchPreviewDisabledReason,
    previewActionRow,
}) => {
    const [copyForm] = Form.useForm();
    const [modal, contextHolder] = Modal.useModal();
    const [batchValue, setBatchValue] = useState("");
    const [timeOffsetOpen, setTimeOffsetOpen] = useState(false);
    const [timeOffsetMinutes, setTimeOffsetMinutes] = useState(null);
    const [lineSwitchModalOpen, setLineSwitchModalOpen] = useState(false);
    const [lineSwitchTargets, setLineSwitchTargets] = useState({});
    const [routeStrategy, setRouteStrategy] = useState("default");
    const [submitting, setSubmitting] = useState(false);
    const [copyModalOpen, setCopyModalOpen] = useState(false);
    const [copySubmitting, setCopySubmitting] = useState(false);
    const [lineSwitchSubmitting, setLineSwitchSubmitting] = useState(false);
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);
    const { selectedCellCount, affectedFlightIds, affectedColumns } = useMemo(() => computeSelectionSummary(selectedCells, rows), [rows, selectedCells]);
    const selectedFlights = useMemo(() => getSelectedFlights(rows, affectedFlightIds), [affectedFlightIds, rows]);
    const batchColumn = affectedColumns.length === 1 ? affectedColumns[0] : null;
    const batchColumnLabel = batchColumn ? getSelectionColumnLabel(batchColumn) : null;
    const selectedAirportPairGroups = useMemo(() => buildSelectedAirportPairGroups(selectedFlights), [selectedFlights]);
    const isBatchEditableColumn = batchColumn ? BATCH_EDITABLE_COLUMNS.has(batchColumn) : false;
    const isAircraftBatchColumn = batchColumn === AIRCRAFT_BATCH_COLUMN;
    const { aircrafts, isLoading: aircraftsLoading } = useAircrafts({ enabled: isAircraftBatchColumn });
    const isTextBatchColumn = batchColumn ? TEXT_BATCH_COLUMNS.has(batchColumn) : false;
    const isAirportBatchColumn = batchColumn ? AIRPORT_BATCH_COLUMNS.has(batchColumn) : false;
    const isStcBatchColumn = batchColumn === STC_BATCH_COLUMN;
    const operationsBusy = submitting || copySubmitting || lineSwitchSubmitting || deleteSubmitting;
    const hasCurrentEditingTrack = currentEditingTrackId != null && currentEditingTrackId !== "";

    useEffect(() => {
        setBatchValue("");
        setRouteStrategy("default");
    }, [batchColumn]);

    useEffect(() => {
        if (!copyModalOpen) {
            return;
        }

        // period_start 是 UTC 语义（响应体为带 Z 的 instant），必须按 UTC 取日历日，不响应设备本地时区
        const parsedStartDate = defaultCopyStartDate ? dayjs.utc(defaultCopyStartDate) : null;
        copyForm.setFieldsValue({
            startDate: parsedStartDate?.isValid() ? parsedStartDate : dayjs.utc(),
            copyCount: 1,
        });
    }, [copyForm, copyModalOpen, defaultCopyStartDate]);

    useEffect(() => {
        if (!lineSwitchModalOpen) {
            return;
        }

        setLineSwitchTargets((current) => buildLineSwitchTargets(selectedAirportPairGroups, current));
    }, [lineSwitchModalOpen, selectedAirportPairGroups]);

    const handleConfirmBatchEdit = useCallback(async () => {
        if (!canUpdateFlight || !draftId || !executeCommand || !batchColumn || !isBatchEditableColumn || !affectedFlightIds.length || (!isStcBatchColumn && !String(batchValue ?? "").length)) {
            return;
        }

        setSubmitting(true);
        try {
            if (isAirportBatchColumn) {
                const normalizedAirport = normalizeAirportCode(batchValue);
                if (!onBatchChangeAirport || normalizedAirport.length !== 3) {
                    message.warning("请输入机场三字码");
                    return;
                }

                const preview = await onBatchChangeAirport({
                    flightIds: affectedFlightIds,
                    columnKey: batchColumn,
                    airport: normalizedAirport,
                    routeStrategy,
                    selectedCellCount,
                });
                if (preview) {
                    setBatchValue("");
                }
                return;
            }

            const normalizedBatchValue = isStcBatchColumn ? normalizeStcTypeEmptyValue(batchValue) : batchValue;
            const result = await executeCommand({
                type: BATCH_UPDATE_FLIGHT_FIELDS,
                draftId,
                payload: {
                    updates: affectedFlightIds.map((flightId) => ({
                        flightId,
                        payload: {
                            [batchColumn]: normalizedBatchValue,
                        },
                    })),
                    label: `批量修改${batchColumnLabel ? ` ${batchColumnLabel}` : ""} ${affectedFlightIds.length} 个航班`,
                },
                meta: {
                    source: "timetable-batch-action-bar",
                    selectedCellCount,
                },
            });

            const changedFlightCount = new Set((result?.changeSet?.changes || []).map((change) => String(change.flightId ?? change.flight_id))).size;
            if (result?.status === "success" && changedFlightCount > 0) {
                message.success(`已批量修改 ${changedFlightCount} 个航班`);
                onClearSelection?.();
            } else {
                message.warning("批量修改未应用");
            }
        } catch (error) {
            message.error(error.response?.data?.detail || error.message || "批量修改失败");
        } finally {
            setSubmitting(false);
        }
    }, [affectedFlightIds, batchColumn, batchColumnLabel, batchValue, canUpdateFlight, draftId, executeCommand, isAirportBatchColumn, isBatchEditableColumn, isStcBatchColumn, onBatchChangeAirport, onClearSelection, routeStrategy, selectedCellCount]);

    const handleConfirmTimeOffset = useCallback(async () => {
        const offsetMinutes = Number(timeOffsetMinutes);
        if (!canUpdateFlight || !onBatchShiftTime || !affectedFlightIds.length || !Number.isFinite(offsetMinutes) || offsetMinutes === 0) {
            return;
        }

        setSubmitting(true);
        try {
            const preview = await onBatchShiftTime({
                flightIds: affectedFlightIds,
                offsetMinutes,
                selectedCellCount,
            });
            if (preview) {
                setTimeOffsetOpen(false);
                setTimeOffsetMinutes(null);
            }
        } catch (error) {
            message.error(error.response?.data?.detail || error.message || "时间偏移失败");
        } finally {
            setSubmitting(false);
        }
    }, [affectedFlightIds, canUpdateFlight, onBatchShiftTime, selectedCellCount, timeOffsetMinutes]);

    const handleOpenLineSwitchModal = useCallback(() => {
        if (!canUpdateFlight || !onBatchSwitchLine || affectedFlightIds.length < 2 || operationsBusy || batchPreviewDisabledReason) {
            return;
        }

        setLineSwitchTargets(buildLineSwitchTargets(selectedAirportPairGroups));
        setLineSwitchModalOpen(true);
    }, [affectedFlightIds.length, batchPreviewDisabledReason, canUpdateFlight, onBatchSwitchLine, operationsBusy, selectedAirportPairGroups]);

    const handleCancelLineSwitchModal = useCallback(() => {
        if (lineSwitchSubmitting) {
            return;
        }

        setLineSwitchModalOpen(false);
        setLineSwitchTargets({});
    }, [lineSwitchSubmitting]);

    const handleLineSwitchTargetChange = useCallback((groupKey, field, value) => {
        setLineSwitchTargets((current) => ({
            ...current,
            [groupKey]: {
                ...(current[groupKey] || {}),
                [field]: normalizeAirportCode(value),
            },
        }));
    }, []);

    const handleConfirmLineSwitch = useCallback(async () => {
        if (!canUpdateFlight || !onBatchSwitchLine || affectedFlightIds.length < 2 || !selectedAirportPairGroups.length) {
            return;
        }

        const mappings = selectedAirportPairGroups.map((group) => ({
            currentDepartureAirport: group.departureAirport,
            currentArrivalAirport: group.arrivalAirport,
            targetDepartureAirport: lineSwitchTargets[group.key]?.targetDepartureAirport,
            targetArrivalAirport: lineSwitchTargets[group.key]?.targetArrivalAirport,
        }));
        const readiness = getLineSwitchReadiness(selectedAirportPairGroups, lineSwitchTargets);
        if (readiness.hasInvalidInput) {
            message.warning("已填写的目标机场需为三字码");
            return;
        }
        if (!readiness.hasEffectiveChange) {
            message.warning("至少填写一个与原机场不同的目标机场");
            return;
        }

        setLineSwitchSubmitting(true);
        try {
            const preview = await onBatchSwitchLine({
                flightIds: affectedFlightIds,
                mappings,
                selectedCellCount,
            });
            if (preview) {
                setLineSwitchModalOpen(false);
                setLineSwitchTargets({});
            }
        } catch (error) {
            message.error(error.response?.data?.detail || error.message || "切换线路失败");
        } finally {
            setLineSwitchSubmitting(false);
        }
    }, [affectedFlightIds, canUpdateFlight, lineSwitchTargets, onBatchSwitchLine, selectedAirportPairGroups, selectedCellCount]);

    const handleDeleteFlights = useCallback(() => {
        if (!canDeleteFlight || !draftId || !executeCommand || !affectedFlightIds.length || deleteSubmitting) {
            return;
        }

        modal.confirm({
            title: "确认批量删除航班？",
            content: (
                <Space direction="vertical" size={4}>
                    <Text>将标记删除 {affectedFlightIds.length} 个航班。</Text>
                    <Text type="secondary">保存前可通过撤销恢复。</Text>
                </Space>
            ),
            okText: "确认删除",
            cancelText: "取消",
            okButtonProps: { danger: true },
            onOk: async () => {
                setDeleteSubmitting(true);
                try {
                    const result = await executeCommand({
                        type: BATCH_DELETE_FLIGHTS,
                        draftId,
                        payload: {
                            flightIds: affectedFlightIds,
                            change_status: "DELETED",
                            label: `批量删除 ${affectedFlightIds.length} 个航班`,
                        },
                        meta: {
                            source: "timetable-batch-action-bar",
                            selectedCellCount,
                        },
                    });
                    const deletedCount = new Set((result?.changeSet?.changes || [])
                        .filter((change) => change.field === "change_status")
                        .map((change) => String(change.flightId ?? change.flight_id))).size;
                    onClearSelection?.();
                    if (result?.status === "success" && deletedCount > 0) {
                        message.success(`已标记删除 ${deletedCount} 个航班`);
                    } else {
                        message.warning("批量删除未应用");
                    }
                } catch (error) {
                    onClearSelection?.();
                    message.error(formatScheduleDraftError(error, "批量删除失败"));
                } finally {
                    setDeleteSubmitting(false);
                }
            },
        });
    }, [affectedFlightIds, canDeleteFlight, deleteSubmitting, draftId, executeCommand, modal, onClearSelection, selectedCellCount]);

    const handleOpenCopyModal = useCallback(() => {
        if (!canUpdateFlight || !draftId || !hasCurrentEditingTrack || !selectedFlights.length || copySubmitting) {
            return;
        }

        setCopyModalOpen(true);
    }, [canUpdateFlight, copySubmitting, draftId, hasCurrentEditingTrack, selectedFlights.length]);

    const handleCancelCopyModal = useCallback(() => {
        if (copySubmitting) {
            return;
        }

        setCopyModalOpen(false);
        copyForm.resetFields();
    }, [copyForm, copySubmitting]);

    const handleConfirmCopyFlights = useCallback(async () => {
        if (!canUpdateFlight || !draftId || !executeCommand || !hasCurrentEditingTrack || !selectedFlights.length) {
            return;
        }

        const values = await copyForm.validateFields();
        const startDate = values.startDate;
        const copyCount = Number(values.copyCount || 0);
        if (!startDate?.isValid?.() || copyCount < 1 || copyCount > 12) {
            return;
        }

        setCopySubmitting(true);
        const expectedCount = selectedFlights.length * copyCount;
        try {
            const copiedFlights = [];
            for (let copyIndex = 0; copyIndex < copyCount; copyIndex += 1) {
                for (const flight of selectedFlights) {
                    const flightId = createClientFlightId();
                    copiedFlights.push({
                        ...buildCopiedFlightValues(flight, startDate, copyIndex, currentEditingTrackId),
                        id: flightId,
                    });
                }
            }

            const result = await executeCommand({
                type: BATCH_ADD_FLIGHTS,
                draftId,
                payload: {
                    flights: copiedFlights,
                    label: `复制 ${selectedFlights.length} 个航班，共新增 ${expectedCount} 个航班`,
                },
                meta: {
                    source: "timetable-copy-flight",
                    trackId: currentEditingTrackId,
                },
            });
            const createdCount = new Set((result?.changeSet?.changes || []).map((change) => String(change.flightId ?? change.flight_id))).size;

            onClearSelection?.();
            setCopyModalOpen(false);
            copyForm.resetFields();
            if (result?.status === "success" && createdCount === expectedCount) {
                message.success(`已复制 ${selectedFlights.length} 个航班，共新增 ${createdCount} 个本地航班，保存后写入后端`);
            } else if (createdCount > 0) {
                message.warning(`已新增 ${createdCount} / ${expectedCount} 个本地航班，保存后写入后端`);
            } else {
                message.warning("复制航班未应用");
            }
        } catch (error) {
            message.error(formatScheduleDraftError(error, "复制航班失败"));
        } finally {
            setCopySubmitting(false);
        }
    }, [canUpdateFlight, copyForm, currentEditingTrackId, draftId, executeCommand, hasCurrentEditingTrack, onClearSelection, selectedFlights]);

    const hasSelection = selectedCellCount > 0;
    const hasPreviewActionRow = Boolean(previewActionRow);
    if (!hasSelection && !hasPreviewActionRow) {
        return null;
    }

    const normalizedAirport = normalizeAirportCode(batchValue);
    const normalizedStcType = normalizeStcTypeValue(batchValue);
    const hasBatchValue = isStcBatchColumn ? true : isAirportBatchColumn ? normalizedAirport.length === 3 : String(batchValue ?? "").length > 0;
    const showBatchStcWarning = isStcBatchColumn && normalizedStcType !== "" && !isRecommendedStcType(normalizedStcType);
    const previewBlocked = Boolean(batchPreviewDisabledReason);
    const confirmDisabled = !canUpdateFlight || operationsBusy || previewBlocked || !isBatchEditableColumn || !hasBatchValue || (isAirportBatchColumn && !onBatchChangeAirport);
    const copyDisabled = !canUpdateFlight || operationsBusy || previewBlocked || !executeCommand || !hasCurrentEditingTrack || !selectedFlights.length;
    const deleteDisabled = !canDeleteFlight || operationsBusy || !executeCommand || !affectedFlightIds.length;
    const timeOffsetDisabled = !canUpdateFlight || operationsBusy || !affectedFlightIds.length || !onBatchShiftTime || Boolean(batchPreviewDisabledReason);
    const timeOffsetConfirmDisabled = timeOffsetDisabled || !Number.isFinite(Number(timeOffsetMinutes)) || Number(timeOffsetMinutes) === 0;
    const lineSwitchDisabled = !canUpdateFlight || operationsBusy || previewBlocked || affectedFlightIds.length < 2 || !onBatchSwitchLine;
    const lineSwitchReadiness = getLineSwitchReadiness(selectedAirportPairGroups, lineSwitchTargets);
    const lineSwitchConfirmDisabled = lineSwitchDisabled || lineSwitchReadiness.hasInvalidInput || !lineSwitchReadiness.hasEffectiveChange;
    const timeOffsetContent = (
        <Space direction="vertical" size={8} style={{ width: 220 }}>
            <Text type="secondary">偏移分钟数（正=推迟，负=提前）</Text>
            <InputNumber
                size="small"
                value={timeOffsetMinutes}
                precision={0}
                style={{ width: "100%" }}
                placeholder="例如 30 或 -45"
                disabled={timeOffsetDisabled}
                onChange={setTimeOffsetMinutes}
                onPressEnter={handleConfirmTimeOffset}
            />
            <Button size="small" type="primary" block loading={submitting} disabled={timeOffsetConfirmDisabled} onClick={handleConfirmTimeOffset}>
                生成预览
            </Button>
        </Space>
    );
    const batchEditControls = batchColumn ? (
        <Space wrap size={8}>
            <Tag color="blue">影响 {affectedFlightIds.length} 个航班</Tag>
            <Text type="secondary">{batchColumnLabel}</Text>
            {isBatchEditableColumn ? (
                <>
                    {isAirportBatchColumn ? (
                        <>
                            <Input
                                size="small"
                                className="schedule-draft-timetable-batch-input"
                                value={batchValue}
                                maxLength={3}
                                placeholder="机场三字码"
                                disabled={!canUpdateFlight || operationsBusy || Boolean(batchPreviewDisabledReason)}
                                onChange={(event) => setBatchValue(normalizeAirportCode(event.target.value))}
                                onPressEnter={handleConfirmBatchEdit}
                            />
                            <Radio.Group
                                size="small"
                                options={ROUTE_STRATEGY_OPTIONS}
                                value={routeStrategy}
                                disabled={!canUpdateFlight || operationsBusy || Boolean(batchPreviewDisabledReason)}
                                onChange={(event) => setRouteStrategy(event.target.value)}
                            />
                        </>
                    ) : isAircraftBatchColumn ? (
                        <AircraftSelector
                            size="small"
                            allowClear
                            className="schedule-draft-timetable-batch-input"
                            value={batchValue || undefined}
                            valueType="registration"
                            displayMode="detailed"
                            aircrafts={aircrafts}
                            loading={aircraftsLoading}
                            placeholder="选择飞机"
                            disabled={!canUpdateFlight || operationsBusy}
                            onChange={(value) => setBatchValue(value || "")}
                            popupMatchSelectWidth={false}
                            style={{ width: 220 }}
                        />
                    ) : isTextBatchColumn ? (
                        <Input
                            size="small"
                            className="schedule-draft-timetable-batch-input"
                            value={batchValue}
                            placeholder="统一修改为"
                            disabled={!canUpdateFlight || operationsBusy}
                            onChange={(event) => setBatchValue(event.target.value)}
                            onPressEnter={handleConfirmBatchEdit}
                        />
                    ) : isStcBatchColumn ? (
                        <Space direction="vertical" size={2} className="schedule-draft-timetable-batch-stc-control">
                            <AutoComplete
                                size="small"
                                allowClear
                                aria-label="批量 STC"
                                className="schedule-draft-timetable-batch-input"
                                value={batchValue}
                                options={RECOMMENDED_STC_TYPE_OPTIONS}
                                placeholder="输入 STC，留空可清空"
                                disabled={!canUpdateFlight || operationsBusy}
                                onChange={(nextValue) => setBatchValue(normalizeStcTypeValue(nextValue))}
                                onInputKeyDown={(event) => {
                                    if (event.key === "Enter") {
                                        event.preventDefault();
                                        event.stopPropagation();
                                        handleConfirmBatchEdit();
                                    }
                                }}
                                filterOption={(inputValue, option) => String(option?.value || "").includes(normalizeStcTypeValue(inputValue))}
                            />
                            {showBatchStcWarning ? <Text type="warning" className="schedule-draft-timetable-batch-warning">{STC_CUSTOM_VALUE_WARNING}</Text> : null}
                        </Space>
                    ) : (
                        <Tag>当前列暂不支持批量修改</Tag>
                    )}
                    <Button size="small" type="primary" loading={submitting} disabled={confirmDisabled} onClick={handleConfirmBatchEdit} style={{ fontSize: "12px" }}>
                        确认修改
                    </Button>
                </>
            ) : (
                <Tag>当前列暂不支持批量修改</Tag>
            )}
        </Space>
    ) : (
        <Tag color="blue">影响 {affectedFlightIds.length} 个航班</Tag>
    );

    return (
        <>
            {contextHolder}
            <BatchActionsBar
                visible={hasSelection || hasPreviewActionRow}
                showSelectionSummary={hasSelection}
                selectedCount={affectedFlightIds.length}
                itemName="个航班"
                onClearSelection={onClearSelection}
                actions={hasSelection ? [
                    { key: "copy-flights", label: "复制航班", disabled: copyDisabled, loading: copySubmitting, showCount: false, onClick: handleOpenCopyModal },
                    { key: "delete-flights", label: "批量删除", danger: true, disabled: deleteDisabled, loading: deleteSubmitting, showCount: false, onClick: handleDeleteFlights },
                    {
                        key: "time-offset",
                        showCount: false,
                        render: () => (
                            <Popover trigger="click" placement="bottom" open={timeOffsetOpen} content={timeOffsetContent} onOpenChange={(open) => setTimeOffsetOpen(open && !timeOffsetDisabled)}>
                                <Button size="small" type="primary" ghost disabled={timeOffsetDisabled} loading={submitting} style={{ fontSize: "12px" }}>
                                    时间偏移
                                </Button>
                            </Popover>
                        ),
                    },
                    { key: "switch-line", label: "切换线路", disabled: lineSwitchDisabled, loading: lineSwitchSubmitting, showCount: false, tooltip: affectedFlightIds.length < 2 ? "至少选择 2 个航班" : undefined, onClick: handleOpenLineSwitchModal },
                ] : []}
                extra={hasSelection ? batchEditControls : null}
            >
                {hasPreviewActionRow ? <div className="schedule-draft-timetable-preview-action-row">{previewActionRow}</div> : null}
            </BatchActionsBar>
            <Modal
                title="切换线路"
                open={lineSwitchModalOpen}
                onCancel={handleCancelLineSwitchModal}
                onOk={handleConfirmLineSwitch}
                okText="生成预览"
                cancelText="取消"
                confirmLoading={lineSwitchSubmitting}
                okButtonProps={{ disabled: lineSwitchConfirmDisabled }}
                destroyOnClose
            >
                <Space direction="vertical" size={12} style={{ width: "100%" }}>
                    <Text type="secondary">已按当前起降机场对分组；目标出发/到达可只填一侧，留空保留原机场，两侧留空跳过该组。</Text>
                    <Tag color="blue">影响 {affectedFlightIds.length} 个航班，{selectedAirportPairGroups.length} 组机场对</Tag>
                    {lineSwitchReadiness.hasInvalidInput ? <Text type="danger">已填写的目标机场需为三字码。</Text> : null}
                    {!lineSwitchReadiness.hasEffectiveChange ? <Text type="secondary">至少填写一个与原机场不同的目标机场后可生成预览。</Text> : null}
                    {selectedAirportPairGroups.map((group) => {
                        const target = lineSwitchTargets[group.key] || {};
                        return (
                            <div key={group.key} className="schedule-draft-line-switch-group">
                                <Space wrap size={8} align="center">
                                    <Tag>{group.count} 个 {formatAirportPair(group.departureAirport, group.arrivalAirport)}</Tag>
                                    <Input
                                        size="small"
                                        value={target.targetDepartureAirport || ""}
                                        maxLength={3}
                                        placeholder="目标出发"
                                        disabled={lineSwitchSubmitting}
                                        style={{ width: 110 }}
                                        onChange={(event) => handleLineSwitchTargetChange(group.key, "targetDepartureAirport", event.target.value)}
                                    />
                                    <Text type="secondary">→</Text>
                                    <Input
                                        size="small"
                                        value={target.targetArrivalAirport || ""}
                                        maxLength={3}
                                        placeholder="目标到达"
                                        disabled={lineSwitchSubmitting}
                                        style={{ width: 110 }}
                                        onChange={(event) => handleLineSwitchTargetChange(group.key, "targetArrivalAirport", event.target.value)}
                                    />
                                </Space>
                            </div>
                        );
                    })}
                </Space>
            </Modal>
            <Modal
                title="复制航班"
                open={copyModalOpen}
                onCancel={handleCancelCopyModal}
                onOk={handleConfirmCopyFlights}
                okText="确认复制"
                cancelText="取消"
                confirmLoading={copySubmitting}
                destroyOnClose
            >
                <Form form={copyForm} layout="vertical">
                    <Form.Item label="影响航班" style={{ marginBottom: 12 }}>
                        <Tag color="blue">{selectedFlights.length} 个航班</Tag>
                    </Form.Item>
                    <Form.Item name="startDate" label="起始日期" rules={[{ required: true, message: "请选择起始日期" }]}>
                        <DatePicker style={{ width: "100%" }} />
                    </Form.Item>
                    <Form.Item name="copyCount" label="复制次数" rules={[{ required: true, message: "请输入复制次数" }, { type: "number", min: 1, max: 12, message: "复制次数需在 1-12 之间" }]}>
                        <InputNumber min={1} max={12} precision={0} style={{ width: "100%" }} />
                    </Form.Item>
                    <Text type="secondary">每次按 7 天递增，STD/STA 保留原航班时分；新航班写入当前活动编辑轨。</Text>
                </Form>
            </Modal>
        </>
    );
};

export default TimetableBatchActionBar;
